import { pipe } from 'fp-ts/function';
import { useCallback } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import { InputValue } from '@optimus-web/core';
import { useCustomContext } from './context';
import { getErrorComponent, getFieldStateError } from './errorHandler';

type BaseControllerProps = Pick<UseControllerProps, 'defaultValue' | 'name' | 'rules' | 'shouldUnregister'>;

const useFormController = (props: BaseControllerProps) => {
    const {
        field: { value, onChange: controllerOnChange, onBlur },
        fieldState,
    } = useController(props);
    const error = pipe(fieldState, getFieldStateError, getErrorComponent);

    return { controllerOnChange, error, onBlur, value };
};

export interface FieldAdapter<V = InputValue> {
    /** @returns value that gets stored in the form context */
    input?: (value: V) => unknown;
    /** @returns value that gets shown in the UI */
    output?: (value: unknown) => V;
}

export interface ControllerProps<V = InputValue> extends BaseControllerProps {
    transform?: FieldAdapter<V>;
}

interface UseControllerWithEventHandlerProps<E, V> extends ControllerProps<V> {
    onChange?: (e: E, value: V) => void;
}

export const useControllerWithEventHandler = <E, V>({
    onChange,
    transform,
    ...controllerProps
}: UseControllerWithEventHandlerProps<E, V>) => {
    const { controllerOnChange, error, onBlur, value } = useFormController(controllerProps);
    const context = useCustomContext();

    const handleChange = useCallback(
        (e: E, newValue: V) => {
            const transformedValue = transform?.input ? transform.input(newValue) : newValue;
            controllerOnChange(transformedValue);
            onChange?.(e, transformedValue as V);
        },
        [controllerOnChange, onChange, transform]
    );

    return { error, handleChange, onBlur, value: transform?.output ? transform.output(value) : value, context };
};

interface UseControllerHandlerProps<V> extends ControllerProps<V> {
    onChange?: (value: V) => void;
}

export const useControllerHandler = <V,>({ onChange, transform, ...controllerProps }: UseControllerHandlerProps<V>) => {
    const { controllerOnChange, error, onBlur, value } = useFormController(controllerProps);
    const context = useCustomContext();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent | null, newValue: V) => {
        const transformedValue = transform?.input ? transform.input(newValue) : newValue;
        controllerOnChange(transformedValue);
        onChange?.(transformedValue as V);
    };

    return { error, handleChange, onBlur, value: transform?.output ? transform.output(value) : value, context };
};
